import warningIcon from "@assets/icons/warning.svg";
import Icon from "@components/global/new/Icon/Icon";
import Selector from "@components/global/new/Selector/Selector";
import WithModal, { CommonModalPropsI } from "@components/HOC/withModals";
import { MODALS_ENUM } from "@constants/Modals";
import { useWatchNetwork } from "@mobx/services/http";
import { RackStore } from "@mobx/store/locations/interfaces";
import { IRackTemplate, TypePartialRackItem3 } from "@mobx/store/locations/types";
import { checkMinerInDeletedSections3 } from "@mobx/utils/rackModalUtils";
import { useFirstRender } from "@utils/hooks/useFirstRender";
import { useRackTemplateTypes } from "@utils/hooks/useRackTemplateTypes";
import { InputNumber, Tabs, TabsProps, theme } from "antd";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import RackEdit3 from "../RackEdit3";
import styles from "./RackModal.module.scss";

export interface IRackModal3PropsBase {
  readonly rackTemplate?: IRackTemplate;
  readonly rackStore?: RackStore;
  readonly otherRackBLIs?: Set<string>;
  readonly hideRackId?: boolean;
  update?(items: TypePartialRackItem3[], type: [number, number], bli?: string): void;
}

export interface IRackModal3Props extends IRackModal3PropsBase, CommonModalPropsI {}

export type ActionType3 = "SELECT" | "OWN";

const INITIAL_TYPE: [number, number] = [10, 2];

const useControl = (
  props: IRackModal3Props
) => {
  const rack = props.rackStore?.rack;
  const firstRender = useFirstRender();
  const lastSelectedRackType = useRef<[number, number]>(
    rack?.rack_type || props.rackTemplate?.rack_type || INITIAL_TYPE
  );
  const { templateTypes, templateTypesStrings } = useRackTemplateTypes();
  const [rackAction, setRackAction] = useState<ActionType3>(
    !!rack || props.rackTemplate ? "OWN" : "SELECT"
  );
  const [rackType, setRackType] = useState<[number, number]>(
    rack?.rack_type ||
      props.rackTemplate?.rack_type ||
      templateTypes[0] ||
      INITIAL_TYPE
  );
  const [widthValue, setWidthValue] = useState(
    rack?.rack_type[1] ||
      props.rackTemplate?.rack_type[1] ||
      INITIAL_TYPE[1]
  );
  const [heightValue, setHeightValue] = useState(
    rack?.rack_type[0] ||
      props.rackTemplate?.rack_type[0] ||
      INITIAL_TYPE[0]
  );
  const [fieldsError, setFieldsError] = useState(false);
  const [reversed, _] = useState(false);
  const [rackBLI, setRackBLI_] = useState(props.rackStore?.rack.rack_id)
  useWatchNetwork(props.rackStore);
  const [bliError, setBLIError] = useState<string>();
  const { formatMessage } = useIntl();

  const onChangeRackAction = (action: ActionType3) => {
    switch (action) {
      case "SELECT":
        setRackType(lastSelectedRackType.current);
        break;
      case "OWN":
        setRackType([heightValue, widthValue]);
        break;
    }
    setRackAction(action);
  };

  const onChangeRackType = (type: string) => {
    const [w, h] = type.split("x").map((item) => +item) as [number, number];
    setRackType([h, w]);
    lastSelectedRackType.current = [h, w];
  };

  useEffect(() => {
    if (firstRender) return;
    setFieldsError(false);
    if (
      rack &&
      widthValue * heightValue !==
        rack.rack_type[0] * rack.rack_type[1] &&
      checkMinerInDeletedSections3(rack.rack_items, rack.rack_type, [
        heightValue,
        widthValue,
      ])
    ) {
      setFieldsError(true);
      return;
    }

    const t = setTimeout(() => setRackType([heightValue, widthValue]), 200);
    return () => clearTimeout(t);
  }, [widthValue, heightValue]);

  useEffect(() => {
    if (firstRender) return;
    if (rackAction === "SELECT") {
      const t = templateTypes[0] || INITIAL_TYPE;
      setRackType(t);
    }
  }, [rackAction]);

  return {
    templateTypesStrings: !templateTypesStrings.length
      ? ["2x10"]
      : templateTypesStrings,
    rackAction,
    rackType,
    widthValue,
    heightValue,
    lastSelectedRackType: lastSelectedRackType.current,
    fieldsError,
    reversed,
    rack,
    rackBLI,
    bliError,

    onChangeRackAction,
    onChangeRackType,
    setWidthValue,
    setHeightValue,
    setRackBLI(bli: string) {
      if (bli.trim() === '' || isNaN(+bli) || bli.length > 3) {
        setBLIError(formatMessage({id: "notifications.error.locations.rack.wrong_name_format"}));
        return;
      }
      bli = bli.padStart(3, '0');
      if (props.otherRackBLIs?.size && props.otherRackBLIs.has(bli)) {
        setBLIError(formatMessage({id: "notifications.error.locations.rack.name_already_in_use"}));
        return;
      }
      setRackBLI_(bli);
      setBLIError(undefined);
    },
  };
};

const RackModal3 = observer((props: IRackModal3Props) => {
  const { formatMessage } = useIntl();
  const ctrl = useControl(props);
  const title = ctrl.rack
    ? `${formatMessage({ id: "modal.rack.title.edit" })} #${ctrl.rackBLI}`
    : props.rackTemplate
    ? `${formatMessage({ id: "modal.rack.title.edit_available" })} ${
        props.rackTemplate.rack_type[1]
      }x${props.rackTemplate.rack_type[0]}`
    : formatMessage({ id: "modal.rack.title.create" });
  const { token } = theme.useToken();
  const st = {
    fontFamily: token.fontFamily,
    fontSize: token.fontSize,
    fontWeight: "bold", 
    color: token.colorTextDescription,
  };

  const items: TabsProps["items"] = [
    {
      key: "SELECT" as ActionType3,
      label: formatMessage({ id: "modal.rack.select.type" }),
      children: (
        <Selector
          className={styles.selector}
          options={ctrl.templateTypesStrings}
          onChange={(e: any) => ctrl.onChangeRackType(e.target.value)}
        />
      ),
      disabled: !!ctrl.rack,
    },
    {
      key: "OWN" as ActionType3,
      label: formatMessage({ id: "modal.rack.select.own" }),
      children: (
        <div className={styles.own_type}>
          <InputNumber
            min={1}
            className={styles.input}
            value={ctrl.widthValue}
            onChange={(value) => ctrl.setWidthValue(value || 1)}
            type='number'
            status={
              ctrl.widthValue === 0 || ctrl.fieldsError
                ? "error"
                : undefined
            }
          />
          <InputNumber
            min={1}
            className={styles.input}
            value={ctrl.heightValue}
            onChange={(value) => ctrl.setHeightValue(value || 1)}
            type='number'
            status={
              ctrl.heightValue === 0 || ctrl.fieldsError
                ? "error"
                : undefined
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.content}>
        {!props.hideRackId && (
          <div className={cn(styles.paramsGroup, {[styles.inactive]: props.otherRackBLIs === undefined})}>
            <label style={st}>{formatMessage({id: "modal.rack.label.enter_rack_id"})}</label>
            <input type="text" defaultValue={props.rackStore?.rack.rack_id} maxLength={3} onChange={(ev) => ctrl.setRackBLI(ev.target.value)}/>
            {props.otherRackBLIs === undefined && 
              <div className={styles.warning}>
                <Icon src={warningIcon}/>
                <span>{formatMessage({id: "modal.rack.warning.remove_miners"})}</span>
              </div>
            }
          </div>
        )}
        <div className={styles.header}>
          <Tabs
            defaultActiveKey={ctrl.rackAction}
            items={items}
            onChange={(key) => ctrl.onChangeRackAction(key as ActionType3)}
          />
        </div>
        <RackEdit3
          type={ctrl.rackType}
          lastType={ctrl.lastSelectedRackType}
          rackTemplate={props.rackTemplate}
          rackStore={props.rackStore}
          rackTypeError={ctrl.fieldsError ? "#" : ctrl.bliError}
          onClose={props.handleClose}
          reversed={ctrl.reversed}
          update={(item, type) => props.update?.(item, type, ctrl.rackBLI)}
        />
      </div>
    </div>
  );
})

RackModal3.displayName = "RackModal3";

export default WithModal(MODALS_ENUM.RACK_MODAL_MOBX, RackModal3);
